/**
 * Auth Logic Layer — Login
 *
 * Owns: submitCredentials, submitTOTP, submitNewPassword,
 *       setPendingSessionFromResponse.
 *
 * Rules:
 *  - No React, no navigate(), no notifications.
 *  - Updates store + localStorage via session helpers.
 *  - Returns outcome objects so the UI decides steps / navigation.
 */
import {
  loginService,
  verifyTOTPService,
  verifyEmailCodeService,
  setupTOTPService,
  authMethodSelector,
} from "../api/services/authServices";
import {
  authenticationOptions,
  authenticationVerification,
  registrationOptions,
  registrationVerification,
} from "./legacyBiometrics";
import { updatePasswordService } from "../api/services/usersServices";
import { getDefaultAuthenticatedPath } from "../modules/appModuleAccess";
import {
  writePendingTokens,
  promotePendingSession,
} from "./session";
import { getPolicyConsentStatus } from "./policy";
import useAuthStore from "../stores/useAuthStore";
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";

// ─── Outcome constants ─────────────────────────────────────────────────────
export const Outcomes = Object.freeze({
  SESSION_START: "session_start",
  TWO_FA_REQUIRED: "two_fa_required",
  PASSWORD_RESET_REQUIRED: "password_reset_required",
  TOTP_SETUP_REQUIRED: "totp_setup_required",
  TOTP_SETUP_COMPLETE: "totp_setup_complete",
  MOBILE_BIOMETRICS_REQUIRED: "mobile_biometrics_required",
  POLICY_REDIRECT: "policy_redirect",
  AUTH_METHOD_SETUP: "auth_method_setup",
  AUTH_METHOD_AUTHENTICATOR: "auth_method_authenticator",
  AUTH_METHOD_EMAIL: "auth_method_email",
  AUTH_METHOD_INVALID: "auth_method_invalid",
  BIO_SUCCESS: "bio_success",
  BIO_ERROR: "bio_error",
  ERROR: "error",
});

// ─── Internal: handle pending → policy check → promote ──────────────────────

/**
 * After receiving a successful login/2FA response that contains an access_token,
 * write pending tokens, check policy, and either promote or signal policy redirect.
 *
 * @param {object} response  API response with access_token, session, user
 * @param {{ isMobile?: boolean, deferPromotion?: boolean }} options
 * @returns {Promise<{ outcome: string, redirectPath?: string, response?: object }>}
 */
async function initializeSession(response, { isMobile = false, deferPromotion = false } = {}) {
  // Post-login landing from module access policy (evaluator / misconfigured / default).
  const landingPage = getDefaultAuthenticatedPath(response?.user);

  writePendingTokens(response, landingPage);

  try {
    const policyStatus = await getPolicyConsentStatus();

    if (policyStatus?.needs_prompt) {
      useAuthStore.getState().setAuthBootstrapStatus("policy_pending");
      return {
        outcome: Outcomes.POLICY_REDIRECT,
        redirectPath: "/terms-and-conditions",
        response,
      };
    }
  } catch (err) {
    console.error("Policy status check failed — promoting anyway:", err);
  }

  if (deferPromotion) {
    return {
      outcome: Outcomes.SESSION_START,
      redirectPath: sessionStorage.getItem("postLoginRedirect") || landingPage,
      response,
    };
  }

  const { redirectPath } = promotePendingSession();
  return {
    outcome: Outcomes.SESSION_START,
    redirectPath,
    response,
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Submit email + password credentials.
 *
 * @param {{ email: string, password: string }} creds
 * @param {{ isMobile?: boolean }} options
 * @returns {Promise<{ outcome: string, response?: object, redirectPath?: string }>}
 */
export async function submitCredentials({ email, password }, { isMobile = false } = {}) {
  try {
    const response = await loginService(email, password, isMobile);

    if (response?.access_token && response?.session?._id) {
      /* Promote immediately so store has user before navigate; deferPromotion is only for 2FA success flash. */
      return initializeSession(response, { isMobile, deferPromotion: false });
    }

    if (
      isMobile &&
      !response?.user?.totp_enabled
    ) {
      return {
        outcome: Outcomes.MOBILE_BIOMETRICS_REQUIRED,
        response,
      };
    }

    const SELECT_METHOD =
      "User authenticated, please select an authentication method";
    if (response?.message === SELECT_METHOD) {
      return { outcome: Outcomes.TWO_FA_REQUIRED, response };
    }

    return { outcome: Outcomes.ERROR, error: "Unexpected login response" };
  } catch (error) {
    return { outcome: Outcomes.ERROR, error };
  }
}

/**
 * Submit a TOTP / email code for 2FA verification.
 *
 * @param {{ email: string, code: string, method: 'authenticator' | 'email', resetPassword?: boolean, remember?: boolean }} params
 * @param {{ isMobile?: boolean, deferPromotion?: boolean }} options
 * @returns {Promise<{ outcome: string, response?: object, redirectPath?: string }>}
 */
export async function submitTOTP(
  { email, code, method, resetPassword = false, remember = false },
  { isMobile = false, deferPromotion = false } = {},
) {
  try {
    const serviceFn = method === "authenticator" ? verifyTOTPService : verifyEmailCodeService;

    const response = await serviceFn(email, code, resetPassword, remember);
    const resetRequiredByServer = response?.user?.reset_password_required === true;

    const RESET_PASSWORD_MSG = "TOTP setup completed, please reset your password";
    const TOTP_SET_UP_COMPLETE = "TOTP setup completed, please log in again";

    if (
      resetRequiredByServer ||
      response?.message === RESET_PASSWORD_MSG ||
      (resetPassword && response?.verified)
    ) {
      return {
        outcome: Outcomes.PASSWORD_RESET_REQUIRED,
        response: resetRequiredByServer ? response : undefined,
      };
    }

    if (isMobile && localStorage.getItem("skipBiometrics") !== "true") {
      return {
        outcome: Outcomes.MOBILE_BIOMETRICS_REQUIRED,
        response,
      };
    }

    if (response?.message === TOTP_SET_UP_COMPLETE) {
      return { outcome: Outcomes.TOTP_SETUP_COMPLETE, response };
    }

    if (response?.access_token) {
      return initializeSession(response, { isMobile, deferPromotion });
    }

    return { outcome: Outcomes.ERROR, error: "Unexpected 2FA response" };
  } catch (error) {
    return { outcome: Outcomes.ERROR, error };
  }
}

/**
 * Submit a TOTP code specifically for the initial authenticator setup.
 *
 * @param {{ email: string, totpInitialCode: string }} params
 * @param {{ isMobile?: boolean }} options
 */
export async function submitTOTPSetup(
  { email, totpInitialCode },
  { isMobile = false } = {},
) {
  try {
    const response = await setupTOTPService(email, totpInitialCode);
    const resetRequiredByServer = response?.user?.reset_password_required === true;

    const RESET_PASSWORD_MSG = "TOTP setup completed, please reset your password";
    const TOTP_SET_UP_COMPLETE = "TOTP setup completed, please log in again";

    if (resetRequiredByServer || response?.message === RESET_PASSWORD_MSG) {
      return {
        outcome: Outcomes.PASSWORD_RESET_REQUIRED,
        response: resetRequiredByServer ? response : undefined,
      };
    }

    if (isMobile && localStorage.getItem("skipBiometrics") !== "true") {
      return {
        outcome: Outcomes.MOBILE_BIOMETRICS_REQUIRED,
        response,
      };
    }

    if (response?.message === TOTP_SET_UP_COMPLETE) {
      return { outcome: Outcomes.TOTP_SETUP_COMPLETE, response };
    }

    if (response?.access_token) {
      return initializeSession(response, { isMobile });
    }

    return { outcome: Outcomes.ERROR, error: "Unexpected TOTP-setup response" };
  } catch (error) {
    return { outcome: Outcomes.ERROR, error };
  }
}

/**
 * Submit a new password (password-reset flow).
 * If an authRequestResponse is provided the layer will also initialize
 * the session afterwards (the user was forced to reset on login).
 *
 * @param {{ email: string, newPassword: string, authRequestResponse?: object }} params
 * @param {{ isMobile?: boolean }} options
 * @returns {Promise<{ outcome: string, redirectPath?: string }>}
 */
export async function submitNewPassword(
  { email, newPassword, authRequestResponse },
  { isMobile = false } = {},
) {
  try {
    await updatePasswordService(email, newPassword);

    if (authRequestResponse) {
      return initializeSession(authRequestResponse, { isMobile });
    }

    return { outcome: "password_updated" };
  } catch (error) {
    return { outcome: Outcomes.ERROR, error };
  }
}

/**
 * Write pending tokens from an already-obtained login response.
 * Useful when coming back from biometrics or stored response.
 *
 * @param {object} response  API response with access_token, session, user
 * @param {{ landingPath?: string, isMobile?: boolean }} options
 * @returns {Promise<{ outcome: string, redirectPath?: string }>}
 */
export async function setPendingSessionFromResponse(
  response,
  { landingPath, isMobile = false } = {},
) {
  return initializeSession(response, { isMobile });
}

// ─── Auth method selection ──────────────────────────────────────────────────

/**
 * Ask the server which 2FA method to use and return an outcome the UI
 * can branch on to set the correct step.
 *
 * @param {{ email: string, method: string, resetPassword?: boolean, isMobile?: boolean }} params
 * @returns {Promise<{ outcome: string, totpUri?: string }>}
 */
export async function selectAuthMethod({ email, method, resetPassword = false, isMobile = false }) {
  try {
    const response = await authMethodSelector(email, method, resetPassword);
    const message = (response?.message ?? response ?? "").toString();

    const SET_UP_REQUIRED = "TOTP setup required";
    const CANT_RESET_PWD = "Cannot reset password via totp";
    const AUTHENTICATED = "User authenticated, please enter TOTP";
    const CODE_SENT_PREFIX = "Code sent to";

    if (message === SET_UP_REQUIRED && response?.totp_uri) {
      if (isMobile) {
        return { outcome: Outcomes.MOBILE_BIOMETRICS_REQUIRED, totpUri: response.totp_uri };
      }
      return { outcome: Outcomes.AUTH_METHOD_SETUP, totpUri: response.totp_uri };
    }

    if (message === AUTHENTICATED) {
      return { outcome: Outcomes.AUTH_METHOD_AUTHENTICATOR };
    }

    if (message.startsWith(CODE_SENT_PREFIX)) {
      return { outcome: Outcomes.AUTH_METHOD_EMAIL };
    }

    if (message === CANT_RESET_PWD) {
      return { outcome: Outcomes.AUTH_METHOD_INVALID };
    }

    return { outcome: Outcomes.ERROR, error: "Unexpected auth method response" };
  } catch (error) {
    return { outcome: Outcomes.ERROR, error };
  }
}

// ─── Biometric handlers ─────────────────────────────────────────────────────

/**
 * Register biometrics for the user, then initialize their session.
 *
 * @param {{ userId: string, email: string, authRequestResponse: object }} params
 * @returns {Promise<{ outcome: string, redirectPath?: string }>}
 */
export async function registerBiometrics({ userId, email, authRequestResponse }) {
  try {
    const options = await registrationOptions(userId, email, null);
    const attResp = await startRegistration({ optionsJSON: options });
    const response = await registrationVerification(attResp, userId);

    if (response && response.status) {
      localStorage.setItem("skipBiometrics", "true");
      return initializeSession(authRequestResponse, { isMobile: true });
    }

    return { outcome: Outcomes.BIO_ERROR };
  } catch (error) {
    return { outcome: Outcomes.BIO_ERROR, error };
  }
}

/**
 * Authenticate via biometrics and initialize the session.
 *
 * @param {{ authRequestResponse: object }} params
 * @returns {Promise<{ outcome: string, redirectPath?: string }>}
 */
export async function loginWithBiometrics({ authRequestResponse }) {
  try {
    const loginOptions = await authenticationOptions();
    const loginAssertion = await startAuthentication({ optionsJSON: loginOptions });
    const response = await authenticationVerification({ credential: loginAssertion });

    if (response?.access_token) {
      return initializeSession(authRequestResponse, { isMobile: true });
    }

    return { outcome: Outcomes.BIO_ERROR };
  } catch (error) {
    return { outcome: Outcomes.BIO_ERROR, error };
  }
}

/**
 * Skip biometrics and initialize the session directly.
 *
 * @param {{ authRequestResponse: object, dontShowAgain?: boolean }} params
 * @returns {Promise<{ outcome: string, redirectPath?: string }>}
 */
export async function skipBiometrics({ authRequestResponse, dontShowAgain = false }) {
  if (dontShowAgain) localStorage.setItem("skipBiometrics", "true");
  return initializeSession(authRequestResponse, { isMobile: true });
}
